// src/pages/MonthlyExpensesPage.js - Enhanced with Weekly Sync (Accounts Section Removed)
import React, { useState, useEffect, useRef } from 'react';
import { useBudget } from '../context/BudgetContext';
import { MonthlyExpensesPrint } from '../utils/printUtils';
import { getStatusAmount } from '../utils/expenseUtils';
import { ALL_WEEKS } from '../utils/constants';
import MoneyInput from '../components/ui/MoneyInput';
import ExpenseSearch from '../components/ui/ExpenseSearch';
import { formatCurrencyValueForInput, normalizeCurrencyValue } from '../utils/currencyInput';
import { getPlannerKey } from '../utils/plannerKeys';

const CATEGORY_NAMES = {
  housing: 'Housing',
  taxes: 'Taxes',
  utilities: 'Utilities',
  insurance: 'Insurance',
  banking: 'Banking',
  loans: 'Loans',
  credit: 'Credit',
  subscriptions: 'Subscriptions',
  food: 'Food',
  transportation: 'Transportation',
  medical: 'Medical',
  personal: 'Personal',
  shopping: 'Shopping',
  dog: 'Dog',
  maintenance: 'Maintenance',
  gifts: 'Gifts'
};

const MonthlyExpensesPage = () => {
  const { state, actions, calculations, formatCurrency } = useBudget();
  const [showZeroValues, setShowZeroValues] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(ALL_WEEKS);
  const [draggingId, setDraggingId] = useState(null);
  const pendingScrollCategory = useRef(null);

  const getPlannerStatus = (expense, statusType) => {
    const key = getPlannerKey(expense);
    const entry = state.data.plannerState?.[key] || (expense?.name ? state.data.plannerState?.[expense.name] : undefined);
    const statuses = entry?.[statusType] || [];
    if (currentWeek === ALL_WEEKS) {
      return statuses.some(Boolean);
    }
    return statuses[currentWeek - 1] || false;
  };

  // Auto-populate planner when monthly data changes
  useEffect(() => {
    if (state.data.monthly && Object.keys(state.data.monthly).length > 0) {
      // Trigger auto-population of planner
      actions.autoPopulatePlanner();
    }
  }, [state.data.monthly]);

  // Handle expense input changes
  const handleExpenseChange = (categoryKey, expenseIndex, field, value, options = {}) => {
    const { normalize = true } = options;
    const category = state.data.monthly[categoryKey] || [];
    const expense = category[expenseIndex] || {};

    // Date edits go through the single authoritative code path so the
    // Planner's week placement moves with the date (see utils/dueDateChange).
    if (field === 'date') {
      const result = actions.updateExpenseDueDate(
        { collection: 'monthly', categoryKey, index: expenseIndex, id: expense.id, name: expense.name },
        value
      );
      if (!result.ok) {
        window.alert(result.error || 'Could not save the date.');
      }
      return;
    }

    let newValue = (field === 'actual' || field === 'projected') && normalize
      ? normalizeCurrencyValue(value)
      : value;

    if (field === 'autoDistributeToPlanner') {
      newValue = value === true;
    }

    const updatedExpense = {
      ...expense,
      [field]: newValue,
      id: expense.id || `${categoryKey}-${Date.now()}-${expenseIndex}`
    };

    actions.updateMonthlyExpense(categoryKey, updatedExpense, expenseIndex);

    if (field === 'autoDistributeToPlanner') {
      // Rebuild planner rows immediately when the checkbox toggles
      actions.autoPopulatePlanner();
    }

    // If this is an actual amount change, trigger planner update when auto-distribution is enabled
    if (field === 'actual' && updatedExpense.name && updatedExpense.autoDistributeToPlanner === true) {
      const amountForPlanner = typeof newValue === 'number' ? newValue : 0;
      setTimeout(() => {
        actions.distributeMonthlyToWeekly(updatedExpense, amountForPlanner, updatedExpense.date);
      }, 100);
    }
  };

  // Handle status changes (paid/transferred) with weekly sync
  const handleStatusChange = (categoryKey, expenseIndex, statusType, checked) => {
    const category = state.data.monthly[categoryKey] || [];
    const expense = category[expenseIndex] || {};

    const updatedExpense = {
      ...expense,
      [statusType]: checked,
      id: expense.id || `${categoryKey}-${Date.now()}-${expenseIndex}`
    };

    actions.updateMonthlyExpense(categoryKey, updatedExpense, expenseIndex);

    // Sync with weekly planner for current week
    if (expense.name) {
      const weekIndex = currentWeek === ALL_WEEKS ? null : currentWeek - 1;
      actions.updateExpenseStatus(
        updatedExpense.id,
        expense.name,
        weekIndex,
        statusType,
        checked,
        'monthly'
      );
    }
  };

  const handlePrint = () => {
    const printContent = MonthlyExpensesPrint.generatePrintContent(
      state.data,
      calculations,
      formatCurrency
    );
    MonthlyExpensesPrint.openPrintWindow(printContent, 'Monthly Expenses Report');
  };

  // Add new expense to category
  const addExpense = (categoryKey) => {
    const category = state.data.monthly[categoryKey] || [];
    const newExpense = {
      id: `${categoryKey}-${Date.now()}`,
      name: '',
      actual: 0,
      projected: 0,
      date: '',
      accountId: '',
      paid: false,
      transferred: false,
      transferStatus: 'none',
      autoDistributeToPlanner: false
    };

    actions.updateMonthlyExpense(categoryKey, newExpense, category.length);
  };

  // Remove expense from category
  const removeExpense = (categoryKey, expenseIndex) => {
    actions.removeMonthlyExpense(categoryKey, expenseIndex);
  };

  // Add a new category
  const addCategory = () => {
    const name = prompt('Enter new category name');
    if (name && !state.data.monthly[name]) {
      pendingScrollCategory.current = name;
      actions.addMonthlyCategory(name);
    }
  };

  // Scroll a newly added category to the top of the page so the user can see it
  useEffect(() => {
    const categoryKey = pendingScrollCategory.current;
    if (categoryKey && state.data.monthly?.[categoryKey]) {
      const el = document.querySelector(`.category[data-category="${categoryKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      pendingScrollCategory.current = null;
    }
  }, [state.data.monthly]);

  // Remove an entire category
  const removeCategory = (categoryKey) => {
    if (window.confirm(`Remove category "${categoryKey}"?`)) {
      actions.removeMonthlyCategory(categoryKey);
    }
  };

  const dragItem = useRef();
  const dragOverItem = useRef();

  const handleDragStartItem = (categoryKey, index, id) => {
    dragItem.current = { categoryKey, index };
    setDraggingId(id);
  };

  const handleDragEnterItem = (categoryKey, index) => {
    dragOverItem.current = { categoryKey, index };
  };

  const handleDrop = () => {
    if (!dragItem.current || !dragOverItem.current) return;
    if (dragItem.current.categoryKey !== dragOverItem.current.categoryKey) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const { categoryKey } = dragItem.current;
    const items = Array.from(state.data.monthly[categoryKey] || []);
    const [moved] = items.splice(dragItem.current.index, 1);
    items.splice(dragOverItem.current.index, 0, moved);
    actions.reorderMonthlyExpenses(categoryKey, items);
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingId(null);
  };

  // Copy projected to actual
  const copyProjectedToActual = (categoryKey, expenseIndex) => {
    const category = state.data.monthly[categoryKey] || [];
    const expense = category[expenseIndex] || {};
    const projectedValue = expense.projected;
    const numericProjected = typeof projectedValue === 'number'
      ? projectedValue
      : parseFloat(projectedValue);

    const formattedProjected = Number.isNaN(numericProjected)
      ? ''
      : parseFloat(numericProjected.toFixed(2));

    handleExpenseChange(categoryKey, expenseIndex, 'actual', formattedProjected);
  };

  // Calculate category total. Uses the same `actual || amount` fallback as
  // getTotalMonthlyExpenses so legacy amount-only expenses count here too —
  // otherwise the category subtotals didn't add up to the page total.
  const getCategoryTotal = (categoryKey) => {
    const category = state.data.monthly[categoryKey] || [];
    return category.reduce(
      (total, expense) => total + (parseFloat(expense.actual || expense.amount) || 0),
      0
    );
  };

  // Calculate total monthly expenses
  const getTotalMonthlyExpenses = () => {
    return calculations.getTotalMonthlyExpenses();
  };

  // Compute per-account transfer/paid summaries
  const calculateStatusSummary = () => {
    const requiredTransfers = {};
    const paidAmounts = {};

    Object.values(state.data.monthly || {}).forEach(expenses => {
      if (Array.isArray(expenses)) {
        expenses.forEach(expense => {
          const amount = getStatusAmount(expense);
          if (amount <= 0) return;
          const accId = expense.accountId || 'unassigned';
          if (expense.transferred) {
            requiredTransfers[accId] = (requiredTransfers[accId] || 0) + amount;
          }
          if (expense.paid) {
            paidAmounts[accId] = (paidAmounts[accId] || 0) + amount;
          }
        });
      }
    });

    const accountIds = Array.from(new Set([...Object.keys(requiredTransfers), ...Object.keys(paidAmounts)]));
    const rows = accountIds.map(id => {
      const required = requiredTransfers[id] || 0;
      const paid = paidAmounts[id] || 0;
      return { id, required, paid, net: required - paid };
    });

    const totalRequired = rows.reduce((sum, r) => sum + r.required, 0);
    const totalPaid = rows.reduce((sum, r) => sum + r.paid, 0);
    const balanceAfterPayments = getTotalMonthlyExpenses() - totalPaid;

    return { rows, totalRequired, totalPaid, balanceAfterPayments };
  };

  const statusSummary = calculateStatusSummary();

  // Reset funding for all expenses
  const resetFunding = () => {
    if (!window.confirm('Reset all projected amounts to $0.00?')) return;

    Object.keys(state.data.monthly || {}).forEach(categoryKey => {
      const category = state.data.monthly[categoryKey] || [];
      category.forEach((expense, index) => {
        if (expense.name) {
          handleExpenseChange(categoryKey, index, 'projected', 0);
        }
      });
    });
  };

  // Reset all statuses
  const resetStatuses = () => {
    if (!window.confirm('Reset all payment and transfer statuses?')) return;

    const resetMonthly = Object.fromEntries(
      Object.entries(state.data.monthly || {}).map(([categoryKey, categoryExpenses = []]) => {
        const updatedExpenses = categoryExpenses.map((expense, index) => {
          if (!expense.name) return expense;

          const expenseId = expense.id || `${categoryKey}-${index}`;
          return {
            ...expense,
            id: expenseId,
            paid: false,
            transferred: false,
            transferStatus: 'none'
          };
        });
        return [categoryKey, updatedExpenses];
      })
    );

    const resetAnnual = Object.fromEntries(
      Object.entries(state.data.annual || {}).map(([categoryKey, categoryExpenses = []]) => {
        const updatedExpenses = categoryExpenses.map((expense, index) => {
          if (!expense.name) return expense;

          const expenseId = expense.id || `${categoryKey}-${index}`;
          return {
            ...expense,
            id: expenseId,
            paid: false,
            transferred: false,
            transferStatus: 'none'
          };
        });
        return [categoryKey, updatedExpenses];
      })
    );

    const resetPlannerState = Object.fromEntries(
      Object.entries(state.data.plannerState || {}).map(([expenseName, entry]) => {
        const plannerEntry = entry || {};
        const paidLength = Array.isArray(plannerEntry.paid) && plannerEntry.paid.length > 0
          ? plannerEntry.paid.length
          : 4;
        const transferredLength = Array.isArray(plannerEntry.transferred) && plannerEntry.transferred.length > 0
          ? plannerEntry.transferred.length
          : 4;

        return [
          expenseName,
          {
            ...plannerEntry,
            paid: Array(paidLength).fill(false),
            transferred: Array(transferredLength).fill(false)
          }
        ];
      })
    );

    actions.updateData({
      ...state.data,
      monthly: resetMonthly,
      annual: resetAnnual,
      plannerState: resetPlannerState
    });
  };

  // Searchable expense list for the shared search box; keys match each row's
  // data-expense-id so selecting a match scrolls straight to that row.
  const searchItems = [];
  Object.entries(state.data.monthly || {}).forEach(([catKey, list]) => {
    (list || []).forEach((exp, i) => {
      if (exp?.name && exp.name.trim()) {
        searchItems.push({
          key: exp.id || `${catKey}-row-${i}`,
          label: exp.name,
          sublabel: CATEGORY_NAMES[catKey] || catKey
        });
      }
    });
  });

  // Reveal hidden zero-value rows so the searched row exists to scroll to.
  const revealForSearch = () => setShowZeroValues(true);

  return (
    <div className="main-content monthly-expenses-page no-top-gap">

      <style>{`
      /* Remove inherited top spacing from global/components CSS just on these pages */
        .main-content.no-top-gap { 
          margin-top: 0 !important; 
          padding-top: 8px !important; /* tweak to 0–12px if you want tighter/looser */
        }

        /* Make sure the first heading doesn’t add extra space */
        .monthly-expenses-page .page-title,
        .weekly-planner-page .page-title,
        .annual-expenses-page .page-title {
          margin-top: 0 !important;
        }

        .monthly-expenses-page {
          padding: 20px;
          background-color: var(--bg-primary);
          min-height: 100vh;
        }

        .page-title {
          color: var(--text-primary);
          margin-bottom: 20px;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .page-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 15px;
          background-color: var(--card-bg);
          border-radius: 8px;
          border: 1px solid var(--card-border);
          flex-wrap: wrap;
          gap: 15px;
          box-shadow: var(--card-shadow);
        }

        .toggle-container {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .toggle-label {
          font-weight: 500;
          color: var(--text-primary);
        }

        .action-controls {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .current-week-selector {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
          padding: 15px;
          background: linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05));
          border-radius: 8px;
          border: 1px solid rgba(33, 150, 243, 0.3);
          box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
        }

        .week-select {
          padding: 8px 12px;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          font-size: 0.9rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .week-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .category {
          margin-bottom: 30px;
          border: 1px solid var(--card-border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: var(--card-shadow);
          background: var(--card-bg);
          transition: all 0.2s ease;
        }

        .category:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .category-header {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          color: white;
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .category-name {
          font-size: 1.2rem;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .category-controls {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .category-total {
          font-size: 1.2rem;
          font-weight: bold;
          background: rgba(255, 255, 255, 0.2);
          padding: 8px 16px;
          border-radius: 20px;
          backdrop-filter: blur(10px);
        }

        .add-item-btn, .remove-category-btn {
          background-color: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
          font-weight: bold;
          transition: all 0.2s ease;
        }

        .add-item-btn:hover, .remove-category-btn:hover {
          background-color: rgba(255,255,255,0.3);
          transform: translateY(-1px);
        }

        .subcategory-header {
          display: flex;
          background: var(--bg-secondary);
          padding: 12px;
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border-light);
        }

        .header-date {
          width: 140px;
        }

        .header-name {
          flex: 2;
        }

        .header-status {
          width: 140px;
          text-align: center;
        }

        .header-auto-distribute {
          width: 220px;
          text-align: center;
        }

        .header-amounts {
          width: 220px;
          display: flex;
          justify-content: space-between;
        }

        .subcategory-list {
          background-color: var(--card-bg);
        }

        .subcategory {
          display: flex;
          align-items: center;
          padding: 15px 12px;
          border-bottom: 1px solid var(--border-light);
          gap: 12px;
          transition: background-color 0.2s ease;
        }

        .subcategory:hover {
          background-color: var(--hover-bg);
        }

        .subcategory:last-child {
          border-bottom: none;
        }

        .date-input {
          width: 140px;
          padding: 8px 10px;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          font-size: 0.85rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          transition: all 0.2s ease;
        }

        .date-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .expense-name-input {
          flex: 2;
          padding: 8px 10px;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          font-size: 0.85rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          transition: all 0.2s ease;
        }

        .expense-name-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .account-select {
          width: 140px;
          padding: 8px 10px;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          font-size: 0.85rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .account-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .status-control-group {
          width: 140px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .transfer-status-select {
          width: 90px;
          padding: 4px 6px;
          border: 1px solid var(--input-border);
          border-radius: 4px;
          font-size: 0.8rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          cursor: pointer;
        }

        .status-none {
          background-color: var(--input-bg);
        }

        .status-quarter {
          background-color: #fff3cd;
        }

        .status-half {
          background-color: #d4edda;
        }

        .status-full {
          background-color: #cce5ff;
        }

        .status-paid {
          background-color: #d1ecf1 !important;
          border-color: #bee5eb !important;
        }

        .expense-status-checkboxes {
          display: flex;
          gap: 8px;
        }

        .status-checkbox-label {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          color: var(--text-primary);
        }

        .auto-distribute-toggle {
          width: 220px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-primary);
        }

        .auto-distribute-toggle input {
          width: 18px;
          height: 18px;
        }

        .auto-distribute-label {
          font-size: 0.9rem;
          color: var(--text-secondary);
        }

        .checkbox-label {
          font-weight: bold;
        }

        .transferred-checkbox:checked {
          accent-color: #ffc107;
        }

        .paid-checkbox:checked {
          accent-color: var(--success);
        }

        .amount-input-group {
          width: 220px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .amount-input {
        width: 90px;
        padding: 8px 10px;
        border: 1px solid var(--input-border);
        border-radius: 6px;
        font-size: 0.85rem;
        text-align: right;
        background-color: var(--input-bg);
        color: var(--text-primary);
        transition: all 0.2s ease;
        flex-shrink: 0; /* Add this line */
        box-sizing: border-box; /* Add this line */
      }

        .amount-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .amount-input.has-value {
          background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%) !important;
          border-color: var(--success) !important;
          font-weight: 600 !important;
          color: #155724 !important;
        }

        .copy-to-actual-btn {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          border: none;
          color: white;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.8rem;
          transition: all 0.2s ease;
        }

        .copy-to-actual-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
        }

        .item-delete-btn {
        background: linear-gradient(135deg, var(--danger) 0%, #c82333 100%);
        border: none;
        color: white;
        padding: 6px; /* Changed from 6px 10px */
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.8rem;
        width: 32px; /* Reduced from 35px */
        height: 32px; /* Added fixed height */
        display: flex; /* Added for better centering */
        align-items: center; /* Center vertically */
        justify-content: center; /* Center horizontally */
        transition: all 0.2s ease;
        flex-shrink: 0; /* Prevent shrinking */
        text-align: center;
        line-height: 1;
        overflow: hidden;
        box-sizing: border-box;
      }

        .item-delete-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
        }

        .total {
          font-size: 1.3rem;
          font-weight: bold;
          text-align: center;
          padding: 25px;
          background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%);
          border-radius: 12px;
          margin: 30px 0;
          border: 1px solid var(--card-border);
          box-shadow: var(--card-shadow);
          color: var(--text-primary);
        }

        .page-actions {
          text-align: center;
          margin: 30px 0;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          margin: 0 8px;
          transition: all 0.2s ease;
          text-transform: none;
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          color: white;
        }

        .btn-secondary {
          background: linear-gradient(135deg, var(--btn-secondary-bg) 0%, #545b62 100%);
          color: white;
        }

        .btn-success {
          background: linear-gradient(135deg, var(--success) 0%, #1e7e34 100%);
          color: white;
        }

        .btn-danger {
          background: linear-gradient(135deg, var(--danger) 0%, #c82333 100%);
          color: white;
        }

        /* Status Summary Section Styling */
        .status-summary-card {
          margin: 30px 0;
          padding: 25px;
          background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%);
          border-radius: 12px;
          border: 1px solid var(--card-border);
          box-shadow: var(--card-shadow);
          transition: all 0.2s ease;
        }
        .status-summary-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,.12); transform: translateY(-1px); }
        .status-summary-card h3 {
          margin: 0 0 20px 0; color: var(--text-primary); font-size: 1.3rem; font-weight: 600; text-align: center;
          padding-bottom: 15px; border-bottom: 2px solid var(--primary);
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .status-summary-table {
          width: 100%; border-collapse: collapse; background: var(--card-bg); border-radius: 8px; overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,.08);
        }
        .status-summary-table thead { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); }
        .status-summary-table th {
          padding: 15px 20px; color: #fff; font-weight: 600; font-size: .95rem; text-align: left; border: none;
          text-shadow: 0 1px 2px rgba(0,0,0,.1);
          /* Override the global "th { background: var(--table-header-bg) }" so the
             thead's purple gradient shows through; otherwise the white header text
             sits on a light-gray cell and is invisible. */
          background: transparent;
        }
        .status-summary-table th:last-child { text-align: right; }
        .status-summary-table td {
          padding: 15px 20px; border-bottom: 1px solid var(--border-light); color: var(--text-primary); font-size: .9rem;
          transition: background-color .2s ease;
        }
        .status-summary-table tbody tr:hover { background-color: var(--hover-bg); }
        .status-summary-table tbody tr:last-child td { border-bottom: none; }
        .status-summary-table .amount { text-align: right; font-weight: 600; font-family: 'Courier New', monospace; color: var(--text-primary); }
        .status-summary-table .overall-row {
          background: linear-gradient(135deg, rgba(33,150,243,.08), rgba(33,150,243,.04)); border-top: 2px solid var(--primary);
        }
        .status-summary-table .overall-row td { font-weight: 700; font-size: 1rem; color: var(--primary); border-bottom: none; }
        .status-summary-table .overall-row .amount { color: var(--primary); font-size: 1.1rem; }

        /* Responsive */
        @media (max-width: 768px) {
          .status-summary-card { margin: 20px 0; padding: 20px 15px; }
          .status-summary-table { font-size: .85rem; }
          .status-summary-table th, .status-summary-table td { padding: 12px 10px; }
          .status-summary-table th { font-size: .85rem; }

          @media (max-width: 480px) {
            .status-summary-table, .status-summary-table thead, .status-summary-table tbody,
            .status-summary-table th, .status-summary-table td, .status-summary-table tr { display: block; }
            .status-summary-table thead tr { position: absolute; top: -9999px; left: -9999px; }
            .status-summary-table tr {
              border: 1px solid var(--card-border); border-radius: 8px; margin-bottom: 15px; padding: 15px;
              background: var(--card-bg); box-shadow: 0 2px 4px rgba(0,0,0,.05);
            }
            .status-summary-table td {
              border: none; position: relative; padding: 8px 0 8px 120px; text-align: left !important;
            }
            .status-summary-table td:before {
              content: attr(data-label) ": "; position: absolute; left: 0; width: 110px; padding-right: 10px;
              white-space: nowrap; font-weight: 600; color: var(--text-secondary);
            }
            .status-summary-table .overall-row { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); color: #fff; }
            .status-summary-table .overall-row td, .status-summary-table .overall-row td:before { color: rgba(255,255,255,.9); }
          }
        }

        /* Accents */
        .status-summary-card .amount { position: relative; }
        .status-summary-card .amount:after {
          content: ''; position: absolute; bottom: -2px; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, var(--primary), transparent);
          opacity: 0; transition: opacity .2s ease;
        }
        .status-summary-table tbody tr:hover .amount:after { opacity: 1; }

        /* Optional horizontal scroll aesthetics */
        .status-summary-table { overflow-x: auto; }
        .status-summary-table::-webkit-scrollbar { height: 6px; }
        .status-summary-table::-webkit-scrollbar-track { background: var(--bg-secondary); border-radius: 3px; }
        .status-summary-table::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 3px; }
        .status-summary-table::-webkit-scrollbar-thumb:hover { background: var(--primary-dark); }

        .weekly-sync-indicator {
          margin-top: 30px;
          padding: 20px;
          background: linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%);
          border-radius: 12px;
          border: 1px solid rgba(33, 150, 243, 0.3);
          box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
        }

        .weekly-sync-indicator h4 {
          margin: 0 0 15px 0;
          color: var(--primary);
          font-size: 1.1rem;
        }

        .sync-info {
          font-size: 0.9rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .add-category-wrapper {
          text-align: center;
          margin: 30px 0;
        }

        .add-category-btn {
          background: linear-gradient(135deg, var(--success) 0%, #1e7e34 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-category-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(40, 167, 69, 0.3);
        }

        @media (max-width: 768px) {
          .page-controls {
            flex-direction: column;
            align-items: stretch;
          }
          
          .subcategory {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .subcategory-header {
            display: none;
          }

          .amount-input-group {
            width: 100%;
            justify-content: space-between;
          }

          .status-control-group {
            width: 100%;
            flex-direction: row;
            justify-content: space-between;
          }

          .date-input, .expense-name-input, .account-select {
            width: 100%;
          }
        }
      `}</style>

      <h2 className="page-title">💳 Monthly Expenses</h2>

      {/* Current Week Selector for Status Sync */}
      <div className="current-week-selector">
        <label><strong>Weekly Planner Sync - Current Week:</strong></label>
        <select
          value={currentWeek}
          onChange={(e) => setCurrentWeek(parseInt(e.target.value))}
          className="week-select"
        >
          <option value={ALL_WEEKS}>All Weeks (Month)</option>
          <option value={1}>Week 1</option>
          <option value={2}>Week 2</option>
          <option value={3}>Week 3</option>
          <option value={4}>Week 4</option>
          <option value={5}>Week 5</option>
        </select>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Status changes will sync with {currentWeek === ALL_WEEKS ? 'all weeks' : `Week ${currentWeek}`} in the weekly planner
        </span>
      </div>

      <div className="page-controls">
        <ExpenseSearch items={searchItems} onBeforeNavigate={revealForSearch} />
        <div className="toggle-container">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={showZeroValues}
              onChange={(e) => setShowZeroValues(e.target.checked)}
            />
            Show Zero-Value Items
          </label>
        </div>

        <div className="action-controls">
          <button className="btn btn-secondary" onClick={resetFunding}>
            Reset Funding
          </button>
          <button className="btn btn-danger" onClick={resetStatuses}>
            Reset Statuses
          </button>
          <button className="btn btn-success" onClick={() => window.location.reload()}>
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Expense Categories */}
      <div id="monthly-expense-categories">
          {Object.entries(state.data.monthly || {}).map(([categoryKey, expenses]) => {
          const categoryName = CATEGORY_NAMES[categoryKey] || categoryKey;
          const categoryTotal = getCategoryTotal(categoryKey);

          return (
            <div key={categoryKey} className="category" data-category={categoryKey}>
              <div className="category-header">
                <span className="category-name">{categoryName}</span>
                <div className="category-controls">
                  <span className="category-total">{formatCurrency(categoryTotal)}</span>
                  <button className="add-item-btn" onClick={() => addExpense(categoryKey)}>+
                  </button>
                  <button className="remove-category-btn" onClick={() => removeCategory(categoryKey)} title="Delete Category">
                    ×
                  </button>
                </div>
              </div>

              <div className="subcategory-header">
                <span className="header-drag" style={{ width: '20px' }}></span>
                <span className="header-date">Date</span>
                <span className="header-name">Expense Name</span>
                <span style={{ width: '140px', textAlign: 'center' }}>Account</span>
                <span className="header-status">Status</span>
                <span className="header-auto-distribute">Auto-Distribute</span>
                <div className="header-amounts">
                  <span>Projected</span>
                  <span>Actual</span>
                </div>
                <span style={{ width: '35px' }}></span>
              </div>
              <div className="subcategory-list" onDragOver={(e) => e.preventDefault()}>
                {expenses.map((expense, index) => {
                const actualAmount = parseFloat(expense.actual || 0);
                const projectedAmount = parseFloat(expense.projected || 0);
                const shouldShow = showZeroValues || actualAmount > 0 || expense.name;

                if (!shouldShow) return null;

                return (
                  <div
                    key={expense.id}
                    className="subcategory"
                    data-expense-id={expense.id || `${categoryKey}-row-${index}`}
                    draggable
                    onDragStart={() => handleDragStartItem(categoryKey, index, expense.id)}
                    onDragEnter={() => handleDragEnterItem(categoryKey, index)}
                    onDragEnd={handleDrop}
                    style={{ backgroundColor: draggingId === expense.id ? '#f0f0f0' : undefined }}
                  >
                    <span className="drag-handle">⋮⋮</span>
                    <input
                      type="date"
                      className="date-input"
                      value={expense.date || ''}
                      onChange={(e) => handleExpenseChange(categoryKey, index, 'date', e.target.value)}
                    />

                    <input
                      className="expense-name-input"
                      value={expense.name || ''}
                      placeholder="Expense name"
                      onChange={(e) => handleExpenseChange(categoryKey, index, 'name', e.target.value)}
                    />

                    <select
                      className="account-select"
                      value={expense.accountId || ''}
                      onChange={(e) => handleExpenseChange(categoryKey, index, 'accountId', e.target.value)}
                    >
                      <option value="">Select Account</option>
                      {(Array.isArray(state.data.accounts) ? state.data.accounts : []).map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>

                    <div className="status-control-group">
                      <select
                        className={`transfer-status-select status-${expense.transferStatus || 'none'}${expense.paid ? ' status-paid' : ''}`}
                        value={expense.transferStatus || 'none'}
                        onChange={(e) => handleExpenseChange(categoryKey, index, 'transferStatus', e.target.value)}
                      >
                        <option value="none">--</option>
                        <option value="quarter">¼</option>
                        <option value="half">½</option>
                        <option value="full">Full</option>
                        <option value="actual">Actual</option>
                      </select>

                      <div className="expense-status-checkboxes">
                        <label className="status-checkbox-label">
                          <input
                            type="checkbox"
                            className="transferred-checkbox"
                            checked={getPlannerStatus(expense, 'transferred')}
                            onChange={(e) => handleStatusChange(categoryKey, index, 'transferred', e.target.checked)}
                          />
                          <span className="checkbox-label">T</span>
                        </label>
                        <label className="status-checkbox-label">
                          <input
                            type="checkbox"
                            className="paid-checkbox"
                            checked={getPlannerStatus(expense, 'paid')}
                            onChange={(e) => handleStatusChange(categoryKey, index, 'paid', e.target.checked)}
                          />
                          <span className="checkbox-label">P</span>
                        </label>
                      </div>
                    </div>

                    <label className="auto-distribute-toggle">
                      <input
                        type="checkbox"
                        checked={expense.autoDistributeToPlanner === true}
                        onChange={(e) => handleExpenseChange(categoryKey, index, 'autoDistributeToPlanner', e.target.checked)}
                      />
                      <span className="auto-distribute-label">Auto-distribute to weekly planner</span>
                    </label>

                    <div className="amount-input-group">
                      <MoneyInput
                        className={`amount-input projected-input ${projectedAmount > 0 ? 'has-value' : ''}`}
                        value={expense.projected}
                        placeholder="Projected"
                        formatDisplay={formatCurrencyValueForInput}
                        onCommit={(value) => handleExpenseChange(categoryKey, index, 'projected', value)}
                      />
                      <button
                        className="copy-to-actual-btn"
                        onClick={() => copyProjectedToActual(categoryKey, index)}
                        title="Copy Projected to Actual"
                      >
                        →
                      </button>
                      <MoneyInput
                        className={`amount-input actual-input ${actualAmount > 0 ? 'has-value' : ''}`}
                        value={expense.actual}
                        placeholder="Actual"
                        formatDisplay={formatCurrencyValueForInput}
                        onCommit={(value) => handleExpenseChange(categoryKey, index, 'actual', value)}
                      />
                    </div>

                    <button
                      className="item-delete-btn"
                      onClick={() => removeExpense(categoryKey, index)}
                      title="Delete Expense"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              </div>
            </div>
          );
        })}
          <div className="add-category-wrapper">
            <button className="add-category-btn" onClick={addCategory}>Add Category</button>
          </div>
        </div>

      <div className="page-actions">
        <button className="btn btn-secondary" onClick={() => handlePrint()}>
          🖨️ Print this Page
        </button>
        <button className="btn btn-primary">
          📁 Export Monthly CSV
        </button>
      </div>

      <div className="total">
        Total Monthly Expenses: {formatCurrency(getTotalMonthlyExpenses())}
      </div>

      <div className="status-summary-card">
        <h3>💰 Status Summary</h3>
        <table className="status-summary-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Required Transfers (Hold)</th>
              <th>Paid</th>
              <th>Net to Hold</th>
            </tr>
          </thead>
          <tbody>
            {statusSummary.rows.map(row => {
              const account = (Array.isArray(state.data.accounts) ? state.data.accounts : [])
                .find(a => a.id === row.id);
              const name = account ? account.name : 'Unassigned';
              return (
                <tr key={row.id}>
                  <td data-label="Account">{name}</td>
                  <td data-label="Required Transfers" className="amount">{formatCurrency(row.required)}</td>
                  <td data-label="Paid" className="amount">{formatCurrency(row.paid)}</td>
                  <td data-label="Net to Hold" className="amount">{formatCurrency(row.net)}</td>
                </tr>
              );
            })}
            <tr className="overall-row">
              <td data-label="Account"><strong>Overall</strong></td>
              <td data-label="Required Transfers" className="amount">{formatCurrency(statusSummary.totalRequired)}</td>
              <td data-label="Paid" className="amount">{formatCurrency(statusSummary.totalPaid)}</td>
              <td data-label="Net to Hold" className="amount">{formatCurrency(statusSummary.balanceAfterPayments)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Weekly Sync Information */}
      <div className="weekly-sync-indicator">
        <h4>📊 Weekly Planner Integration</h4>
        <div className="sync-info">
          • Monthly expenses automatically populate the weekly planner<br />
          • Status changes (Paid/Transferred) sync with {currentWeek === ALL_WEEKS ? 'All Weeks' : `Week ${currentWeek}`} in the weekly planner<br />
          • Due dates determine which week expenses are planned for<br />
          • Changes here update the weekly planner in real-time
        </div>
      </div>
    </div>
  );
};

export default MonthlyExpensesPage;